import React, { useEffect, useState } from "react";
import {
  Grid,
  Typography,
  Paper,
  Backdrop,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Avatar,
  Box,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { axiosAPI, axiosupload } from "../../Components/AxiosComponents/Axios";

import { decryptId } from "../../Components/CryptoComponent";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraAltIcon from "@mui/icons-material/CameraAlt";

import TextInput from "../../Components/InputComponents/TextInput";
import SelectInput from "../../Components/InputComponents/SelectInput";
import TextAreaInput from "../../Components/InputComponents/TextAreaInput";
import CommonButton from "../../Components/InputComponents/CommonButton";

import {
  errorNotify,
  infoNotify,
  successNotify,
} from "../../Components/ToastComponent/ToastNotification";

const EditStaff = () => {
  const { id } = useParams();
  const decryptedId = decryptId(decodeURIComponent(id));
  const navigate = useNavigate();
  const location = useLocation();

  const paginationState = location?.state?.pagination || {
    page: 0,
    rowsPerPage: 25,
  };

  const filterData = location?.state?.filterData || {
    name: "",
    passport_no: "",
    profession: "",
    branch: "",
  };

  // State to collect text input data
  const [staffData, setStaffData] = useState({
    ED_Name: null,
    ED_Dateofbirth: null,
    ED_ContactNumber: null,
    ED_Email: null,
    ED_Address: null,

    ED_Department: null,
    ED_Designation: null,
    ED_Branch: null,
    ED_ReportingPerson: null,
    ED_EmploymentStatus: null,
    ED_Dateofjoin: null,
    ED_Referal: null,
    ED_Remarks: null,
    ED_Salary: 0,
    ED_PayFrequency: null,
    ED_MaxLeave: null,
    ED_SickLeave: null,
    ED_BonusInfo: null,

    ED_UserName: null,
    ED_UserStatus: null,
    ED_Role: null,
  });

  const [initialData, setInitialData] = useState(null);

  const [isDisabledTab, setIsDisabledTab] = useState({
    tab0: true,
    tab1: true,
    tab2: true,
    tab3: true,
  });
  const [ED_Photo, setED_Photo] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");

  const tabFields = {
    tab0: [
      "ED_Photo",
      "ED_Name",
      "ED_Dateofbirth",
      "ED_ContactNumber",
      "ED_Email",
      "ED_Address",
    ],
    tab1: [
      "ED_Designation",
      "ED_Department",
      "ED_Branch",
      "ED_ReportingPerson",
      "ED_EmploymentStatus",
      "ED_Dateofjoin",
      "ED_Referal",
      "ED_Remarks",
    ],
    tab2: [
      "ED_Salary",
      "ED_PayFrequency",
      "ED_MaxLeave",
      "ED_SickLeave",
      "ED_BonusInfo",
    ],
    tab3: ["ED_UserName", "ED_UserStatus", "ED_Role"],
  };

  const compareTabs = (newData) => {
    if (!initialData) return;

    const updatedDisabled = { ...isDisabledTab };

    Object.entries(tabFields).forEach(([tabKey, fields]) => {
      const hasChanged = fields.some((field) => {
        const newVal = newData[field] ?? null;
        const oldVal = initialData[field] ?? null;

        return String(newVal) !== String(oldVal);
      });

      updatedDisabled[tabKey] = !hasChanged;
    });

    setIsDisabledTab(updatedDisabled);
  };
  const handlePictureChange = (event) => {
    const file = event.target.files[0];
    const MAX_SIZE_MB = 30;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2); // rounded to 2 decimal places

    if (file.size > MAX_SIZE_BYTES) {
      infoNotify(
        `File size should be less than ${MAX_SIZE_MB} MB. Uploaded file size is ${fileSizeMB} MB`
      );
      return;
    }

    setED_Photo(event.target.files[0]);
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
  };
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    const updatedData = { ...staffData, [name]: value };

    setStaffData(updatedData);

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    compareTabs(updatedData);
  };

  // State for error handling
  const [errors, setErrors] = useState({});

  const [designationList, setDesignationList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [employmentStatus, setEmploymentStatus] = useState([]);
  const [employeeRoleList, setEmployeeRoleList] = useState([]);
  const [reportingPersonList, setReportingPersonList] = useState([]);
  const [salaryPayFreq, setSalaryPayFreq] = useState([]);

  const getDesignation = async () => {
    try {
      const res = await axiosAPI.get(`/Designation/list`);
      setDesignationList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getDepartment = async () => {
    try {
      const res = await axiosAPI.get(`/Department/active/list`);
      setDepartmentList(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/Department/list`);
        setDepartmentList(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  const getBranches = async () => {
    try {
      const res = await axiosAPI.get(`/Branch/active/list`);
      setBranchList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getEmploymentStatus = async () => {
    try {
      const res = await axiosAPI.get(`/EmpStatus/active/list`);
      setEmploymentStatus(res?.data?.data || []);
    } catch (err) {
      console.error("Active API failed, retrying without 'active'", err);
      try {
        const resFallback = await axiosAPI.get(`/EmpStatus/list`);
        setEmploymentStatus(resFallback?.data?.data || []);
      } catch (fallbackErr) {
        console.error("Fallback API also failed", fallbackErr);
      }
    }
  };

  const getEmployeeRoleList = async () => {
    try {
      const res = await axiosAPI.get(`/UserRole/list/active`);
      setEmployeeRoleList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getReportingPersonList = async () => {
    try {
      const res = await axiosAPI.get(`/Employee/reportingstaff`);
      setReportingPersonList(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getSalaryPayList = async () => {
    try {
      const res = await axiosAPI.get(`/SalaryPayFrequency/list`);
      setSalaryPayFreq(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const getData = async () => {
    try {
      const res = await axiosAPI.get(`/Employee/listbyid/${decryptedId}`);
      const dataFromApi = res.data.data;

      setStaffData(dataFromApi);
      setInitialData(dataFromApi);
    } catch (err) {
      console.error(err);
    }
  };

  const mandatoryFields = [
    { field: "ED_Name", label: "Full Name", tab: 0 },
    { field: "ED_Dateofbirth", label: "Date of Birth", tab: 0 },
    {
      field: "ED_ContactNumber",
      label: "Contact Number",
      type: "phone",
      tab: 0,
    },
    { field: "ED_Email", label: "Email Address", type: "email", tab: 0 },

    { field: "ED_Designation", label: "Designation", tab: 1 },
    { field: "ED_Department", label: "Department", tab: 1 },
    { field: "ED_Branch", label: "Branch", tab: 1 },
    { field: "ED_EmploymentStatus", label: "Employment Status", tab: 1 },

    { field: "ED_UserName", label: "Username", tab: 3 },
  ];

  useEffect(() => {
    compareTabs(staffData);
    if (previewUrl !== "") {
      setIsDisabledTab({ ...isDisabledTab, tab0: false });
    }
  }, [staffData, previewUrl]);

  const validateForm = () => {
    const formErrors = {};
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    const phoneRegex = /^[0-9]{20}$/;

    let firstErrorTab = null;

    // Validate required fields and types
    for (let { field, label, type, tab } of mandatoryFields) {
      const value = staffData[field];

      if (!value) {
        formErrors[field] = `${label} is required.`;
        if (firstErrorTab === null) firstErrorTab = tab;
      } else {
        if (type === "email" && !emailRegex.test(value)) {
          formErrors[field] = `Please enter a valid ${label}.`;
          if (firstErrorTab === null) firstErrorTab = tab;
        }

        if (type === "password" && value.length < 8) {
          formErrors[field] = `${label} must have at least 8 characters.`;
          if (firstErrorTab === null) firstErrorTab = tab;
        }
      }
    }

    // Handle Date validation
    if (staffData.ED_Dateofbirth === "Invalid Date") {
      formErrors["ED_Dateofbirth"] = "Date of Birth format is invalid.";
      if (firstErrorTab === null) firstErrorTab = 0;
    }
    if (staffData.ED_Dateofjoin === "Invalid Date") {
      formErrors["ED_Dateofjoin"] = "Date of Joining format is invalid.";
      if (firstErrorTab === null) firstErrorTab = 1;
    }

    setErrors(formErrors);

    if (Object.keys(formErrors).length > 0) {
      if (firstErrorTab !== null) {
        setTabValue(firstErrorTab); // Change tab to first error
      }

      Object.values(formErrors).forEach((error) => {
        errorNotify(error);
      });

      return false;
    }

    return true;
  };

  const [isDisabled, setIsDisabled] = useState(false);
  const [loaderOpen, setLoaderOpen] = useState(false);

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    if (validateForm()) {
      const formData = new FormData();

      if (ED_Photo) {
        formData.append("profilePic", ED_Photo);
        // setstaffData({...staffData, IS_UseExisting_Image:false})
        formData.append(`IS_UseExisting_Image`, false);
      } else if (ED_Photo == null && staffData?.ED_Photo != null) {
        // setstaffData({...staffData, IS_UseExisting_Image:true})
        formData.append(`IS_UseExisting_Image`, true);
      } else if (ED_Photo == null && staffData?.ED_Photo == null) {
        // setstaffData({...staffData, IS_UseExisting_Image:true})
        formData.append("profilePic", null);
        formData.append(`IS_UseExisting_Image`, false);
      } else {
        // setstaffData({...staffData, IS_UseExisting_Image:true})
        formData.append(`IS_UseExisting_Image`, true);
      }

      Object.entries(staffData).forEach(([key, value]) => {
        if (Array.isArray(value)) {
          formData.append(key, JSON.stringify(value));
        } else {
          formData.append(key, value);
        }
      });
      formData.delete("ED_Role");

      formData.append(`ED_Status`, staffData?.ED_UserStatus);
      formData.append(`ED_Role`, staffData?.ED_Role || staffData?.ED_UserType);

      setIsDisabled(true);
      setLoaderOpen(true);
      axiosupload
        ?.post(
          `/Employee/edit/${decryptedId}`,
          formData
          //   {
          //   ED_Name: staffData?.ED_Name,
          //   ED_Email: staffData?.ED_Email,
          //   ED_ContactNumber: staffData?.ED_ContactNumber,
          //   ED_Address: staffData?.ED_Address,
          //   ED_Department: staffData?.ED_Department,
          //   ED_Designation: staffData?.ED_Designation,
          //   ED_Dateofbirth: staffData?.ED_Dateofbirth,
          //   ED_Dateofjoin: staffData?.ED_Dateofjoin,
          //   ED_Branch: staffData?.ED_Branch,
          //   ED_ReportingPerson: staffData?.ED_ReportingPerson
          //     ? staffData?.ED_ReportingPerson
          //     : null,
          //   ED_EmploymentStatus: staffData?.ED_EmploymentStatus,
          //   ED_Salary: staffData?.ED_Salary,
          //   ED_PayFrequency: staffData?.ED_PayFrequency,
          //   ED_BonusInfo: staffData?.ED_BonusInfo,
          //   ED_MaxLeave: staffData?.ED_MaxLeave,
          //   ED_SickLeave: staffData?.ED_SickLeave,
          //   ED_Remarks: staffData?.ED_Remarks,
          //   ED_Referal: staffData?.ED_Referal,
          //   ED_Status: staffData?.ED_UserStatus,
          //   ED_Role: staffData?.ED_UserType,
          // }
        )
        .then((res) => {
          setIsDisabled(false);
          setLoaderOpen(false);

          if (res?.data?.success) {
            successNotify(res?.data?.message);
            navigate("/Staffs", {
              state: { pagination: paginationState, filterData: filterData },
            });
          } else {
            infoNotify(res?.data?.message);
          }
        })
        .catch((err) => {
          errorNotify("Something went wrong!");
          setIsDisabled(false);
          setLoaderOpen(false);
        });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
    getDesignation();
    getDepartment();
    getBranches();
    getEmploymentStatus();
    getEmployeeRoleList();
    getReportingPersonList();
    getSalaryPayList();
  }, []);

  const [tabValue, setTabValue] = useState(0);

  const [tabs, setTabs] = useState([
    "Personal Information",
    "Employment Details",
    "Salary and Leave Details",
    "Login Credentials",
  ]);

  const handleSubChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Paper
      sx={{
        maxWidth: `100vw`,
        padding: 2,
        margin: "auto",
        boxShadow: "none",
        overflowX: "auto",
        fontFamily: "Geist, sans-serif",
        minHeight: "100%",
      }}
    >
      {/* Main Heading ================================================================================================================================================================== */}
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
      >
        Edit Staff
      </Typography>

      {/* Tabs ========================================================================================================================================================================== */}
      <Grid container spacing={2} sx={{ maxWidth: "100vw", overflow: `auto` }}>
        <Grid item>
          <Tabs
            value={tabValue}
            onChange={handleSubChange}
            indicatorColor="error"
            textColor="error"
            variant="scrollable"
            scrollButtons="auto"
            sx={{ fontFamily: "Geist, sans-serif", marginTop: "10px" }}
          >
            {tabs.map((label, index) => (
              <Tab
                key={label}
                label={label}
                sx={{
                  fontFamily: "Geist, sans-serif",
                  background: tabValue == index ? "#006CB8" : "#f5f9ffff",
                  border: "1px solid lightgrey",
                  borderBottom: "4px solid #EAB308",
                  color: tabValue == index ? "white" : "black",
                  fontWeight: tabValue == index ? 600 : 500,
                  fontSize: `14px`,
                  letterSpacing: "1px",
                  minWidth: "150px",
                  minHeight: "60px",
                  marginRight: "5px",
                }}
              />
            ))}
          </Tabs>
        </Grid>
      </Grid>

      {/* Personal Information ========================================================================================================================================================== */}
      {tabValue == 0 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
          }}
        >
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={12}
              md={4}
              lg={4}
              xl={3}
              container
              justifyContent="center"
              alignItems="center"
              sx={{ minHeight: 300 }} // Adjust height as needed
            >
              <Grid item>
                <Box sx={{ position: "relative", display: "inline-block" }}>
                  {ED_Photo && previewUrl ? (
                    <>
                      <label htmlFor="picture-upload">
                        <Avatar
                          src={previewUrl}
                          sx={{
                            width: 250,
                            height: 250,
                            border: "2px solid grey",
                            cursor: "pointer",
                          }}
                        />
                      </label>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setED_Photo(null);
                          setIsDisabledTab({ ...isDisabledTab, tab0: false });
                        }}
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          zIndex: 1,
                          "&:hover": {
                            backgroundColor: "#f8d7da",
                          },
                        }}
                      >
                        <DeleteIcon sx={{ color: "#e74c3c", fontSize: 18 }} />
                      </IconButton>
                    </>
                  ) : staffData?.ED_Photo ? (
                    <>
                      <label htmlFor="picture-upload">
                        <Avatar
                          src={`${process.env.REACT_APP_BASE_URL}/${staffData?.ED_Photo}`}
                          sx={{
                            width: 250,
                            height: 250,
                            border: "2px solid grey",
                            cursor: "pointer",
                          }}
                        />
                      </label>
                      <IconButton
                        size="small"
                        onClick={() => {
                          setED_Photo(null);
                          setStaffData({
                            ...staffData,
                            ED_Photo: null,
                          });
                        }}
                        sx={{
                          position: "absolute",
                          top: -8,
                          right: -8,
                          backgroundColor: "#fff",
                          border: "1px solid #ccc",
                          zIndex: 1,
                          "&:hover": {
                            backgroundColor: "#f8d7da",
                          },
                        }}
                      >
                        <DeleteIcon sx={{ color: "#e74c3c", fontSize: 18 }} />
                      </IconButton>
                    </>
                  ) : (
                    <Avatar
                      sx={{
                        width: 250,
                        height: 250,
                        border: "2px solid grey",
                        cursor: "pointer",
                      }}
                    >
                      <label htmlFor="picture-upload">
                        <IconButton component="span">
                          <CameraAltIcon />
                        </IconButton>
                      </label>
                    </Avatar>
                  )}
                </Box>
                <input
                  accept="image/*"
                  style={{ display: "none" }}
                  id="picture-upload"
                  type="file"
                  onChange={handlePictureChange}
                />
              </Grid>
            </Grid>
            <Grid item xs={12} sm={12} md={8} lg={8} xl={9} container>
              {/*  Full Name */}
              <Grid
                item
                sx={{ padding: "5px" }}
                xs={12}
                sm={6}
                md={6}
                lg={6}
                xl={4}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Full Name <span style={{ color: "red" }}> * </span>
                </Typography>
                <TextInput
                  label="Full Name"
                  placeholder="Enter Full Name"
                  name="ED_Name"
                  value={staffData?.ED_Name}
                  onChange={handleInputChange}
                  error={!!errors.ED_Name}
                />
              </Grid>

              {/* Date of Birth */}
              <Grid
                item
                sx={{ padding: "5px" }}
                xs={12}
                sm={6}
                md={6}
                lg={6}
                xl={4}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Date of Birth <span style={{ color: "red" }}> * </span>
                </Typography>
                <TextInput
                  label="Date of Birth"
                  placeholder="Date of Birth"
                  name="ED_Dateofbirth"
                  value={staffData?.ED_Dateofbirth}
                  onChange={handleInputChange}
                  error={!!errors?.ED_Dateofbirth}
                  type="date"
                  dateRestriction="past" // 'none' | 'future' | 'past'
                />
              </Grid>

              {/*  Contact Number */}
              <Grid
                item
                sx={{ padding: "5px" }}
                xs={12}
                sm={6}
                md={6}
                lg={6}
                xl={4}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Contact Number <span style={{ color: "red" }}> * </span>
                </Typography>
                <TextInput
                  placeholder="Enter Contact Number"
                  type="text"
                  name="ED_ContactNumber"
                  value={staffData?.ED_ContactNumber}
                  onChange={handleInputChange}
                  error={!!errors?.ED_ContactNumber}
                  allowOnlyNumbers={true}
                  maxLength={20}
                />
              </Grid>

              {/* Email */}
              <Grid
                item
                sx={{ padding: "5px" }}
                xs={12}
                sm={6}
                md={6}
                lg={6}
                xl={4}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Email <span style={{ color: "red" }}> * </span>
                </Typography>
                <TextInput
                  label="Email Address"
                  placeholder="Enter Email Address"
                  type="email"
                  name="ED_Email"
                  value={staffData?.ED_Email}
                  onChange={handleInputChange}
                  error={!!errors?.ED_Email}
                />
              </Grid>

              {/* Address */}
              <Grid
                item
                sx={{ padding: "5px" }}
                xs={12}
                sm={6}
                md={6}
                lg={6}
                xl={4}
              >
                <Typography
                  variant="subtitle1"
                  sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
                >
                  Address
                </Typography>
                <TextAreaInput
                  label="Address"
                  placeholder="Enter Address"
                  name="ED_Address"
                  value={staffData?.ED_Address}
                  onChange={handleInputChange}
                />
              </Grid>
            </Grid>
          </Grid>

          {/* Save - Only visible on last tab */}
          <Grid item xs={12} display="flex" justifyContent={"flex-end"}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabledTab.tab0}
            >
              {isDisabled ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Employment Details ============================================================================================================================================================ */}
      {tabValue == 1 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
          }}
        >
          <Grid container spacing={2}>
            {/* Designation */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Designation <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                placeholder="Choose your Designation"
                name="ED_Designation"
                value={staffData?.ED_Designation}
                onChange={handleInputChange}
                error={!!errors?.ED_Designation}
                options={designationList?.map((item) => ({
                  value: item?.DI_Id,
                  label: item?.DI_Name,
                }))}
              />
            </Grid>

            {/* Department */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Department <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                value={staffData.ED_Department}
                name="ED_Department"
                onChange={handleInputChange}
                options={departmentList?.map((item) => ({
                  value: item?.DL_Id,
                  label: item?.DL_Name,
                }))}
                placeholder="Select Department"
                error={!!errors.ED_Department}
              />
            </Grid>

            {/* Branch */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Branch <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                value={staffData.ED_Branch}
                name="ED_Branch"
                onChange={handleInputChange}
                options={branchList?.map((item) => ({
                  value: item?.BL_Id,
                  label: item?.BL_Name,
                }))}
                placeholder="Select Branch"
                error={!!errors.ED_Branch}
              />
            </Grid>

            {/* Supervisor / Manager */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Supervisor / Manager
              </Typography>
              <SelectInput
                value={staffData?.ED_ReportingPerson}
                name="ED_ReportingPerson"
                onChange={handleInputChange}
                options={reportingPersonList?.map((item) => ({
                  value: item?.ED_Id,
                  label: item?.ED_Name,
                }))}
                placeholder="Select Supervisor / Manager"
              />
            </Grid>

            {/* Employment Status */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Employment Status <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                value={staffData?.ED_EmploymentStatus}
                name="ED_EmploymentStatus"
                onChange={handleInputChange}
                options={employmentStatus?.map((item) => ({
                  value: item?.ES_Id,
                  label: item?.ES_Name,
                }))}
                placeholder="Select Employment Status"
                error={!!errors.ED_EmploymentStatus}
              />
            </Grid>

            {/* Date of Joining */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Date of Joining
              </Typography>
              <TextInput
                label="Date of Joining"
                placeholder="Date of Joining"
                name="ED_Dateofjoin"
                value={staffData.ED_Dateofjoin}
                onChange={handleInputChange}
                type="date"
              />
            </Grid>

            {/* Referal */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Referal
              </Typography>
              <TextInput
                label="Referal"
                placeholder="Referal"
                name="ED_Referal"
                value={staffData.ED_Referal}
                onChange={handleInputChange}
              />
            </Grid>

            {/* Internal Notes */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Internal Notes
              </Typography>
              <TextAreaInput
                label="Internal Notes"
                placeholder="Internal Notes"
                name="ED_Remarks"
                value={staffData.ED_Remarks}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          {/* Save - Only visible on last tab */}
          <Grid item xs={12} display="flex" justifyContent={"flex-end"}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabledTab.tab1}
            >
              {isDisabled ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Salary and Leave Details */}
      {tabValue == 2 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
          }}
        >
          <Grid container spacing={2}>
            {/* Salary */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Salary
              </Typography>
              <TextInput
                label="Salary"
                placeholder="Salary"
                name="ED_Salary"
                value={staffData.ED_Salary}
                onChange={handleInputChange}
                allowOnlyNumbers={true}
                maxLength={9}
              />
            </Grid>

            {/*  Pay Frequency  */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Pay Frequency
              </Typography>

              <SelectInput
                value={staffData?.ED_PayFrequency}
                name="ED_PayFrequency"
                onChange={handleInputChange}
                options={salaryPayFreq?.map((item) => ({
                  value: item?.SPFL_Id,
                  label: item?.SPFL_Name,
                }))}
                placeholder="Select Salary Pay Frequency"
              />
            </Grid>

            {/* Maximum Leave (Days)  */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Maximum Leave (Days)
              </Typography>
              <TextInput
                placeholder=" Maximum Leave (Days)"
                name="ED_MaxLeave"
                value={staffData.ED_MaxLeave}
                onChange={handleInputChange}
                allowOnlyNumbers={true}
                maxLength={3}
                capAt={365}
              />
            </Grid>

            {/* Maximum Sick Leaves (Days) */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Maximum Sick Leaves (Days)
              </Typography>
              <TextInput
                placeholder=" Maximum Sick Leave (Days)"
                name="ED_SickLeave"
                value={staffData.ED_SickLeave}
                onChange={handleInputChange}
                allowOnlyNumbers={true}
                maxLength={3}
                capAt={365}
              />
            </Grid>

            {/* Bonus Info */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Bonus Info
              </Typography>
              <TextAreaInput
                label="Bonus Info"
                placeholder="Bonus Info"
                name="ED_BonusInfo"
                value={staffData.ED_BonusInfo}
                onChange={handleInputChange}
              />
            </Grid>
          </Grid>

          {/* Save - Only visible on last tab */}
          <Grid item xs={12} display="flex" justifyContent={"flex-end"}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabledTab.tab2}
            >
              {isDisabled ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Login Credentials ============================================================================================================================================================= */}
      {tabValue == 3 && (
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          sx={{
            border: "1px solid lightgrey",
            padding: "20px",
          }}
        >
          <Grid container spacing={2}>
            {/* Username */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Username <span style={{ color: "red" }}> * </span>
              </Typography>
              <TextInput
                label="Username"
                placeholder="Username"
                name="ED_UserName"
                value={staffData.ED_UserName}
                onChange={handleInputChange}
                error={!!errors.ED_UserName}
                isDisabled={true}
              />
            </Grid>

            {/* Role */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Role <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                value={staffData?.ED_Role || staffData?.ED_UserType}
                name={`ED_Role`}
                onChange={handleInputChange}
                options={employeeRoleList?.map((item) => ({
                  value: item?.RL_Id,
                  label: item?.RL_Name,
                }))}
                placeholder="Select Role"
                error={!!errors.ED_Role}
              />
            </Grid>

            {/* Status */}
            <Grid
              item
              xs={12}
              sm={6}
              md={6}
              lg={4}
              xl={3}
              sx={{ padding: "5px" }}
            >
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 500, fontFamily: "Geist, sans-serif" }}
              >
                Status <span style={{ color: "red" }}> * </span>
              </Typography>
              <SelectInput
                value={staffData?.ED_UserStatus}
                name={`ED_UserStatus`}
                onChange={handleInputChange}
                options={[
                  { value: "1", label: "Active" },
                  { value: "2", label: "Inactive" },
                ]}
                placeholder="Select Status"
                error={!!errors.ED_UserStatus}
              />
            </Grid>
          </Grid>
          {/* Save - Only visible on last tab */}
          <Grid item xs={12} display="flex" justifyContent={"flex-end"}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabledTab.tab3}
            >
              {isDisabled ? "Saving..." : "Save"}
            </Button>
          </Grid>
        </Grid>
      )}

      {/* Buttons ======================================================================================================================================================================= */}
      <Grid container spacing={2} justifyContent="flex-end" sx={{ mt: 3 }}>
        {/* Cancel - Always visible */}
        <Grid item>
          <CommonButton
            variant="outlined"
            customColor="#B0BEC5"
            onClick={() =>
              navigate("/Staffs", {
                state: { pagination: paginationState, filterData: filterData },
              })
            }
          >
            Cancel
          </CommonButton>
        </Grid>

        {/* Back - Visible if not on first tab */}
        {tabValue > 0 && (
          <Grid item>
            <CommonButton
              variant="contained"
              startIcon={<ArrowBackIcon />}
              onClick={() => setTabValue((prev) => prev - 1)}
            >
              Back
            </CommonButton>
          </Grid>
        )}

        {/* Next - Visible if not on last tab */}
        {tabValue < tabs?.length - 1 && (
          <Grid item>
            <CommonButton
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => setTabValue((prev) => prev + 1)}
            >
              Next
            </CommonButton>
          </Grid>
        )}

        {/* Save - Only visible on last tab */}
        {/* {tabValue === tabs?.length - 1 && (
          <Grid item>
            <CommonButton
              variant="contained"
              onClick={handleSubmit}
              disabled={isDisabled}
            >
              {isDisabled ? "Saving..." : "Save"}
            </CommonButton>
          </Grid>
        )} */}

        {/* Loader Backdrop */}
        <Backdrop
          open={loaderOpen}
          sx={{
            zIndex: (theme) => theme.zIndex.drawer + 1,
            color: "#fff",
            backdropFilter: "blur(5px)",
            backgroundColor: "rgba(0, 0, 0, 0.2)",
            flexDirection: "column",
          }}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      </Grid>
    </Paper>
  );
};

export default EditStaff;
